'use client';

import { useCallback, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { EventName, type FormActionPayload, type FormActionType } from '../../Shibuya';
import { useAnalytics } from '..';
import { useAnalyticsPageView } from '../AnalyticsPageView';
import { getCleanLocationHref } from '../urlUtils';

type FireFormActionExtra = {
  field?: string;
  fieldType?: string;
  fieldValue?: string;
  additionalAttributes?: FormActionPayload['additional_attributes'];
};

export function useFormAction(formName: string) {
  const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
  const { pageViewId, pageName, pageViewReady } = useAnalyticsPageView();
  const anchorEventIdRef = useRef('');

  const fireFormAction = useCallback(
    (formAction: FormActionType, extra: FireFormActionExtra = {}) => {
      if (!pageViewReady || !deviceId) return;

      const isViewed = formAction === 'viewed';
      const eventId = uuid();
      if (isViewed) {
        // Start a new funnel for this (re)open.
        anchorEventIdRef.current = eventId;
      }
      const parentEventId = anchorEventIdRef.current;

      const payload: FormActionPayload = {
        deployable_name: deployableName,
        deployable_version: deployableVersion,
        traditional_device_id: deviceId,
        event_id: eventId,
        // The `viewed` anchor parents every later action; `viewed` has no parent.
        ...(!isViewed && parentEventId ? { parent_event_id: parentEventId } : {}),
        form_action: formAction,
        form_name: formName,
        pageview_id: pageViewId,
        page_name: pageName,
        page_url: getCleanLocationHref(),
        ...(extra.field && { field: extra.field }),
        ...(extra.fieldType && { field_type: extra.fieldType }),
        ...(extra.fieldValue !== undefined && { field_value: extra.fieldValue }),
        ...(extra.additionalAttributes && { additional_attributes: extra.additionalAttributes }),
      };
      analytics?.track(EventName.FORM_ACTION, payload, {
        context: { app: { name: deployableName, version: gitSha } },
      });
    },
    [
      analytics,
      deviceId,
      deployableName,
      deployableVersion,
      gitSha,
      formName,
      pageViewId,
      pageName,
      pageViewReady,
    ],
  );

  return { fireFormAction };
}
